import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, LineReveal } from "@/components/Reveal";

const BELIEF_ITEMS = [
  {
    title: "God the Father",
    text: "God the Father is the Creator of the universe. He created man in His own image for fellowship, and called man back to Himself through Christ after the rebellion and fall of man.",
  },
  {
    title: "Jesus Christ",
    text: "Jesus Christ is eternally God. He was together with the Father and the Holy Spirit from the beginning, and through Him all things were made. He left Heaven and became incarnate by the Holy Spirit of the Virgin Mary; henceforth, He is forever one Christ with two natures - God and man - in one Person.",
  },
  {
    title: "The Holy Spirit",
    text: "The Holy Spirit is God, the Lord, and giver of life, who was active in the Old Testament, and given to the Church in fullness at Pentecost. He empowers the saints for service and witness, cleanses man from the old nature, and conforms us to the image of Christ.",
  },
  {
    title: "The Baptism in the Holy Spirit",
    text: "The baptism in the Holy Spirit with evidence of speaking in tongues, subsequent to conversion, releases the fullness of the Spirit, and is evidenced by the fruits and gifts of the Holy Spirit.",
  },
  {
    title: "The Bible",
    text: "The Bible, containing the Old and New Testaments, is alone the only infallible, inspired Word of God, and its authority is ultimate, final, and eternal. It cannot be added to, subtracted from, or superseded in any regard. The Bible is the source of all doctrine, instruction, correction, and reproof. It contains all that is needed for guidance in godliness and practical Christian conduct.",
  },
  {
    title: "The Atonement",
    text: "Christ's vicarious death on the cross paid the penalty for the sins of the whole world. Its benefits of healing - body, soul, and spirit - are provided for in the atonement as well.",
  },
  {
    title: "Salvation",
    text: "Salvation is a free gift of God, based on the merits of the death of His Son, and is appropriated by faith. Salvation is effected by personal repentance, belief in the Lord Jesus (justification), and personal acceptance of Him into one's life as Lord and Savior (regeneration).",
  },
  {
    title: "The Christian Life",
    text: "The Christian life is to be one of consecration, devotion, and holiness. The shortcomings of the individual are because of the still-progressing sanctification of the saints. The Christian life is filled with trials, tests, and warfare against a spiritual enemy. For those abiding in Christ until their deaths or His return, the promises of eternal blessing in the presence of God are assured.",
  },
  {
    title: "The Church",
    text: "The Church is the Body of Christ, the habitation of God among the saints through the Spirit. Every believer born of the Spirit has a place in the church designated by God - a place where Christ is working in the lives of the called-out ones and calling ministers to the Great Commission to go into all the world and make disciples of every nation.",
  },
  {
    title: "Baptism & the Lord's Supper",
    text: "We believe in the ordinances of Baptism and the Lord's Supper. Baptism is the outward sign of what God has already done in the individual's life and is a public testimony that the person now belongs to Christ. It is identifying with the death and resurrection of Jesus and is done in the name of the Father, the Son, and the Holy Spirit. The Lord's Supper is a commemoration of the death of the Lord and is done in remembrance of Him until He comes again; it is a sign of our participation in Him.",
  },
  {
    title: "The Second Coming",
    text: "We believe in the bodily, personal, second coming of the Lord Jesus Christ, the resurrection of the saints, the millennium, and the final judgment. The final judgment will determine the eternal status of both the saints and the unbelievers, determined by their relationship to Jesus Christ.",
  },
];

export default function Beliefs() {
  return (
    <>
      <SEO
        title="What We Believe - Our Statement of Faith"
        description="What Victory Chapel believes: the Trinity, the Bible as God's inspired Word, salvation by faith, the baptism in the Holy Spirit, the Church, baptism and communion, and the second coming of Christ. A Bible-centered church in Clay County, FL."
        path="/beliefs"
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">What We Believe</p>
          <LineReveal
            className="mt-4 font-display text-5xl font-900 leading-[1.0] tracking-tighter sm:text-6xl lg:text-7xl"
            lines={["OUR STATEMENT", "OF FAITH."]}
          />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg" data-testid="beliefs-intro">
              There is only one God, eternally existent in three Persons: Father, Son, and Holy Spirit.
              Everything we believe flows from Him.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-24" data-testid="beliefs-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          {BELIEF_ITEMS.map((item, i) => (
            <Reveal key={item.title} delay={0.05}>
              <article
                className={`grid gap-3 py-8 sm:grid-cols-12 sm:gap-8 sm:py-10 ${i > 0 ? "border-t border-navy/10" : ""}`}
                data-testid={`belief-item-${i + 1}`}
              >
                <div className="sm:col-span-4">
                  <p className="font-display text-4xl font-900 leading-none text-navy/10" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="mt-2.5 font-display text-xl font-800 tracking-tight text-navy sm:text-2xl">
                    {item.title}
                  </h2>
                </div>
                <p className="text-base leading-relaxed text-slate-600 sm:col-span-8 sm:text-lg">{item.text}</p>
              </article>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col items-start gap-5 rounded-3xl bg-navy p-7 text-white sm:flex-row sm:items-center sm:justify-between sm:p-9">
              <div>
                <h2 className="font-display text-xl font-800 tracking-tight">QUESTIONS ABOUT WHAT WE BELIEVE?</h2>
                <p className="mt-2 text-sm text-white/70">We'd love to talk - no pressure, ever.</p>
              </div>
              <Link to="/contact" data-testid="beliefs-contact-cta"
                    className="inline-flex shrink-0 items-center gap-2 rounded-full bg-victory px-7 py-3.5 font-display text-sm font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                ASK US A QUESTION <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
